import { useState, useEffect, useCallback } from 'react'
import {
  type Operation,
  type Difficulty,
  type MathsQuestion,
  generateQuestions,
} from './MathsData'

export type { Operation, Difficulty, MathsQuestion }

export interface MathsState {
  questions:       MathsQuestion[]
  currentIndex:    number
  correctCount:    number
  wrongChoice:     number | null    // the value the user just tapped incorrectly
  correctFlash:    boolean          // brief highlight on correct answer
  moves:           number           // total answer attempts
  startTime:       number | null
  endTime:         number | null
  gameComplete:    boolean
}

function initState(operations: Operation[], difficulty: Difficulty): MathsState {
  return {
    questions:    generateQuestions(operations, difficulty),
    currentIndex: 0,
    correctCount: 0,
    wrongChoice:  null,
    correctFlash: false,
    moves:        0,
    startTime:    null,
    endTime:      null,
    gameComplete: false,
  }
}

function applyAnswer(prev: MathsState, choice: number): MathsState {
  if (prev.wrongChoice !== null || prev.correctFlash) return prev

  const question  = prev.questions[prev.currentIndex]
  const newMoves  = prev.moves + 1
  const startTime = prev.startTime ?? Date.now()

  if (choice !== question.answer) {
    return { ...prev, wrongChoice: choice, moves: newMoves, startTime }
  }

  // Correct
  const newCorrect = prev.correctCount + 1
  const isLast     = prev.currentIndex === prev.questions.length - 1
  const gameComplete = isLast

  return {
    ...prev,
    correctCount: newCorrect,
    correctFlash: true,
    moves:        newMoves,
    startTime,
    gameComplete,
    endTime: gameComplete ? Date.now() : prev.endTime,
  }
}

export function useMaths(operations: Operation[], difficulty: Difficulty) {
  const [state, setState] = useState<MathsState>(() =>
    initState(operations, difficulty)
  )

  const resetGame = useCallback(() => {
    setState(initState(operations, difficulty))
  }, [operations, difficulty])

  // Clear wrong answer flash after 650 ms
  useEffect(() => {
    if (state.wrongChoice === null) return
    const t = setTimeout(() => {
      setState((prev) => ({ ...prev, wrongChoice: null }))
    }, 650)
    return () => clearTimeout(t)
  }, [state.wrongChoice])

  // Advance to next question after correct answer flash
  useEffect(() => {
    if (!state.correctFlash) return
    const t = setTimeout(() => {
      setState((prev) => {
        if (prev.gameComplete) return { ...prev, correctFlash: false }
        return {
          ...prev,
          correctFlash: false,
          currentIndex: prev.currentIndex + 1,
        }
      })
    }, 500)
    return () => clearTimeout(t)
  }, [state.correctFlash])

  const answerQuestion = useCallback((choice: number) => {
    setState((prev) => applyAnswer(prev, choice))
  }, [])

  return { state, resetGame, answerQuestion }
}
